import SentryEchart from "../../../../../../components/echarts/sentry-echart.jsx";
import {memo, useEffect, useMemo, useRef, useState} from "react";
import {isValid, parse} from "date-fns";
import { garrisonTimelineViewLayoutSize as layout } from "../../../utils/garrison-timeline-utils.js";

const createHatchPattern = () => {
  const canvas = document.createElement("canvas");
  canvas.width = 12;
  canvas.height = 12;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#EAEAEA";
  ctx.fillRect(0, 0, 12, 12);
  ctx.strokeStyle = "#B3B3B3";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, 12);
  ctx.lineTo(12, 0);
  ctx.stroke();

  return { image: canvas, repeat: "repeat" };
};

const createOfflinePattern = () => {
  const canvas = document.createElement("canvas");
  canvas.width = 12;
  canvas.height = 12;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#4B5563";
  ctx.fillRect(0, 0, 12, 12);
  ctx.strokeStyle = "#DC2626";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, 12);
  ctx.lineTo(12, 0);
  ctx.stroke();

  return { image: canvas, repeat: "repeat" };
};

const statusColors = {
  "ONLINE": "#808080",
  "DISPATCH": "#008C51",
  "DISPATCH_ACTIVITY": "#287499",
  "OFFLINE": createOfflinePattern(),
  "NOSIGN": createHatchPattern(),
  "SHIFT_BASE": "#FFF0C5"
};

const statusLabels = {
  ONLINE: "Disponível",
  OFFLINE: "Offline",
  DISPATCH: "Em despacho",
  DISPATCH_ACTIVITY: "Em atividade",
  DISPATCH_PARENT: "Em despacho",
  DISPATCH_ACTIVITY_PARENT: "Em atividade",
  NOSIGN: "Sem sinal",
};

const formatTime = (minutes) => {
  const hours = Math.floor(minutes / 60).toString().padStart(2, "0");
  const remainingMinutes = (minutes % 60).toString().padStart(2, "0");

  return `${hours}:${remainingMinutes}`;
};

const escapeHtml = (unsafe) => {
  return (unsafe || "").toString()
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const parseDateSafe = (dateStr) => {
  if (!dateStr) return new Date();

  const ref = new Date();
  ref.setHours(0, 0, 0, 0);

  let parsedDate = parse(dateStr, "dd/MM/yyyy HH:mm:ss", ref);
  if (isValid(parsedDate)) return parsedDate;

  parsedDate = parse(dateStr, "dd/MM/yyyy HH:mm", ref);
  if (isValid(parsedDate)) return parsedDate;

  return new Date(dateStr);
};

const Timeline = ({data, zoom, onZoomChange, onSegmentClick, filter, hideTopBorder = false}) => {
  const chartRef = useRef(null);
  const [chartInstance, setChartInstance] = useState(null);
  const zoomRef = useRef(zoom);
  const onZoomChangeRef = useRef(onZoomChange);
  zoomRef.current = zoom;
  onZoomChangeRef.current = onZoomChange;
  const chartData = useMemo(() => {
    if (!data?.garrisons?.length) return null;

    const filterStart = parseDateSafe(filter?.dtStart);
    const timelineStart = parseDateSafe(data.timelineStart);
    const offsetMinutes = Math.floor(Math.max(0, (timelineStart.getTime() - filterStart.getTime()) / 60000));
    const yCategories = data.garrisons.map((garrison) => garrison.id);
    const seriesData = [];
    const groupedDispatchIds = new Set();
    const dispatchesById = new Map(
      (data.dispatches || []).map(({dispatchId, dispatchActivity, dispatchNature}) => [
        String(dispatchId),
        {dispatchActivity, dispatchNature}
      ])
    );
    const getDispatchNature = (dispatchId) => {
      const dispatch = dispatchesById.get(String(dispatchId));
      return dispatch?.dispatchNature || null;
    };
    const getSegmentStatus = (segment) => {
      if (["OFFLINE", "NOSIGN"].includes(segment.status)) return segment.status;

      const dispatch = segment.dispatchId ? dispatchesById.get(String(segment.dispatchId)) : null;
      const isActivity = dispatch?.dispatchActivity || dispatch?.dispatchNature?.activity || String(dispatch?.dispatchNature || "").toUpperCase() === "ATIVIDADE";

      if (segment.dispatchId) return isActivity ? "DISPATCH_ACTIVITY" : "DISPATCH";

      return "ONLINE";
    };

    data.garrisons.forEach((garrison, index) => {
    const segments = data.ganttSegments?.[garrison.id] || [];

    seriesData.push({
      name: garrison.name,
      value: [
        index,
        (garrison.shiftStartMinute || 0) + offsetMinutes,
        (garrison.shiftEndMinute || 0) + offsetMinutes,
        "SHIFT_BASE",
        null,
        null
      ],
      itemStyle: { color: statusColors["SHIFT_BASE"] }
    });

    const dispatchGroups = {};

    segments.forEach((segment) => {
      if (!segment.dispatchId) return;

      const adjStart = segment.startMinute + offsetMinutes;
      const adjEnd = segment.endMinute + offsetMinutes;
      const segmentStatus = getSegmentStatus(segment);

      if (!dispatchGroups[segment.dispatchId]) {
        dispatchGroups[segment.dispatchId] = {start: adjStart, end: adjEnd, count: 1, status: segmentStatus, color: statusColors[segmentStatus]};
        return;
      }

      dispatchGroups[segment.dispatchId].start = Math.min(dispatchGroups[segment.dispatchId].start, adjStart);
      dispatchGroups[segment.dispatchId].end = Math.max(dispatchGroups[segment.dispatchId].end, adjEnd);
      dispatchGroups[segment.dispatchId].count += 1;
    });

    Object.entries(dispatchGroups).forEach(([dispatchId, group]) => {
      if (group.count <= 1) return;

      groupedDispatchIds.add(String(dispatchId));

      seriesData.push({
        name: garrison.name,
        value: [
          index,
          group.start,
          group.end,
          group.status === "DISPATCH_ACTIVITY" ? "DISPATCH_ACTIVITY_PARENT" : "DISPATCH_PARENT",
          dispatchId,
          getDispatchNature(dispatchId)
        ],
        itemStyle: {
          color: "transparent",
          opacity: 1,
          borderColor: group.color,
          borderWidth: 2,
          borderType: "solid"
        }
      });
    });

    segments.forEach((segment) => {
      const adjStart = segment.startMinute + offsetMinutes;
      const adjEnd = segment.endMinute + offsetMinutes;
      const segmentStatus = getSegmentStatus(segment);
      const groupedDispatchId = segmentStatus === "NOSIGN"
        ? Object.entries(dispatchGroups).find(([, group]) => group.count > 1 && adjStart >= group.start && adjEnd <= group.end)?.[0] || null
        : null;
      const dispatchId = segment.dispatchId || groupedDispatchId;

      seriesData.push({
        name: garrison.name,
        value: [
          index,
          adjStart,
          adjEnd,
          segmentStatus,
          dispatchId,
          getDispatchNature(dispatchId)
        ],
        itemStyle: {
          color: statusColors[segmentStatus] || "#000"
        }
      });
    });
    });

    const firstGarrisonId = data.garrisons[0].id;
    const maxFilteredMinutes = data.timeline?.[firstGarrisonId]
      ? data.timeline[firstGarrisonId].length - 1 + offsetMinutes
      : 1440;

    return {filterStart, garrisons: data.garrisons, groupedDispatchIds, maxFilteredMinutes, offsetMinutes, seriesData, yCategories};
  }, [data, filter?.dtStart]);

  const ROW_HEIGHT = layout.h + 0.1;
  const chartHeight = (data?.garrisons?.length || 0) * ROW_HEIGHT;

  const option = useMemo(() => {
    if (!chartData) return {};

    return {
    tooltip: {
      trigger: "item",
      formatter: (params) => {
        const startMinute = params.value[1];
        const endMinute = params.value[2];
        const status = params.value[3];

        const getFormatted = (mins) => {
          const d = new Date(chartData.filterStart.getTime() + mins * 60000);
          return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
        };

        if (status === "SHIFT_BASE") {
          const garrison = chartData.garrisons[params.value[0]];
          const startTime = garrison?.shiftStart ? garrison.shiftStart.substring(11, 16) : params.value[4]?.substring(11, 16) || "";
          const endTime = garrison?.shiftEnd ? garrison.shiftEnd.substring(11, 16) : params.value[5]?.substring(11, 16) || "";
          return `${params.marker} Turno: ${startTime} às ${endTime}`;
        }

        if (["DISPATCH", "DISPATCH_ACTIVITY", "DISPATCH_PARENT", "DISPATCH_ACTIVITY_PARENT"].includes(status)) {
          const dispatchId = params.value[4];
          const dispatchNature = params.value[5] || "";
          const timeRange = `${getFormatted(startMinute)}~${getFormatted(endMinute)}`;
          const parentColor = status === "DISPATCH_ACTIVITY_PARENT" ? "#287499" : status === "DISPATCH_PARENT" ? "#008C51" : null;
          const marker = parentColor
            ? `<span style="display:inline-block;margin-right:4px;border-radius:50%;width:10px;height:10px;background:${parentColor}"></span>`
            : params.marker;

          return `${marker} ${statusLabels[status]} #${escapeHtml(dispatchId)} - ${escapeHtml(dispatchNature)} ${timeRange}`;
        }

        const statusMarker = status === "OFFLINE"
          ? `<span style="display:inline-block;margin-right:4px;border-radius:50%;width:10px;height:10px;background:repeating-linear-gradient(45deg,#4B5563,#4B5563 4px,#DC2626 4px,#DC2626 5px)"></span>`
          : status === "NOSIGN"
            ? `<span style="display:inline-block;margin-right:4px;border-radius:50%;width:10px;height:10px;background:repeating-linear-gradient(45deg,#EAEAEA,#EAEAEA 4px,#B3B3B3 4px,#B3B3B3 5px)"></span>`
            : params.marker;

        return `${statusMarker} ${statusLabels[status]} - ${getFormatted(startMinute)}~${getFormatted(endMinute)}`;
      },
    },
    grid: { left: 30, right: 14, top: 0, bottom: 0, containLabel: false },
    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: 0,
        start: zoom?.start ?? 0,
        end: zoom?.end ?? 100,
        zoomOnMouseWheel: false,
        moveOnMouseMove: true,
        moveOnMouseWheel: false,
        filterMode: 'none'
      }
    ],
    xAxis: {
      type: 'value',
      position: 'bottom',
      min: 0,
      max: chartData.maxFilteredMinutes,
      splitLine: { show: false },
      axisLine: { show: false },
      axisLabel: { show: false }
    },
    yAxis: {
      type: 'category',
      data: chartData.yCategories,
      inverse: true,
      axisLabel: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false }
    },
    series: [
      {
        type: 'custom',
        renderItem: (params, api) => {
          const categoryIndex = api.value(0);
          const startCoord = api.coord([api.value(1), categoryIndex]);
          const endCoord = api.coord([api.value(2), categoryIndex]);
          const status = api.value(3);
          const isParent = ["DISPATCH_PARENT", "DISPATCH_ACTIVITY_PARENT"].includes(status);
          const isChild = ["DISPATCH", "DISPATCH_ACTIVITY", "NOSIGN"].includes(status) && chartData.groupedDispatchIds.has(String(api.value(4)));

          const barHeight = ROW_HEIGHT * (status === "SHIFT_BASE" ? 0.7 : isChild ? 0.35 : 0.5);
          const width = Math.max(endCoord[0] - startCoord[0], 2);

          let z2 = 2;
          if (status === "SHIFT_BASE") z2 = 0;
          if (isParent) z2 = 1;

          return {
            type: 'rect',
            z2,
            shape: {
              x: startCoord[0],
              y: startCoord[1] - barHeight / 2,
              width: Math.max(width , 2),
              height: barHeight,
              r: [10, 10, 10, 10]
            },
            style: api.style(),
            styleEmphasis: {
              ...api.style(),
              opacity: 0.7
            }
          };
        },
        encode: {
          x: [1, 2],
          y: 0
        },
        data: chartData.seriesData
      }
    ]
    };
  }, [chartData, ROW_HEIGHT, zoom?.end, zoom?.start]);

  const echartsEvents = useMemo(() => {
    return {
    datazoom: (params) => {
      const start = params.batch ? params.batch[0].start : params.start;
      const end = params.batch ? params.batch[0].end : params.end;

      if (onZoomChange && start !== undefined && end !== undefined) {
        if (Math.abs(end - start) < 0.1) return;
        onZoomChange({start, end});
      }
    }
    };
  }, [onZoomChange]);

  useEffect(() => {
    if (!chartInstance) return undefined;

    const handleChartWheel = (event) => {
      if (!event.event?.ctrlKey && !event.event?.metaKey) return;

      const pointInGrid = chartInstance.convertFromPixel(
        {seriesIndex: 0},
        [event.offsetX, event.offsetY]
      );
      const targetMinute = pointInGrid?.[0];
      const totalMinutes = chartData?.maxFilteredMinutes;

      if (targetMinute === undefined || !totalMinutes || event.wheelDelta === 0) return;

      event.stop();
      event.event.preventDefault();

      const currentZoomStart = zoomRef.current?.start ?? 0;
      const currentZoomEnd = zoomRef.current?.end ?? 100;
      const currentZoomRange = currentZoomEnd - currentZoomStart;
      const anchor = (targetMinute / totalMinutes) * 100;
      const pointerRatio = Math.min(Math.max((anchor - currentZoomStart) / currentZoomRange, 0), 1);
      const nextZoomRange = Math.min(100, Math.max(0.5, currentZoomRange * (event.wheelDelta < 0 ? 1.2 : 0.8)));
      const nextZoomStart = Math.min(Math.max(anchor - nextZoomRange * pointerRatio, 0), 100 - nextZoomRange);

      onZoomChangeRef.current?.({start: nextZoomStart, end: nextZoomStart + nextZoomRange});
    };

    const zrender = chartInstance.getZr();
    zrender.on("mousewheel", handleChartWheel);

    return () => {
      zrender.off("mousewheel", handleChartWheel);
    };
  }, [chartData?.maxFilteredMinutes, chartInstance]);

  if (!data?.garrisons?.length) return null;

  return (
    <div
      className={`relative border-r border-[#E2E8F0] w-full ${hideTopBorder ? "" : "border-t"}`}
      style={{ height: `${chartHeight}px` }}
    >
      <div className="absolute inset-0 z-0 flex flex-col pointer-events-none">
        {data.garrisons.map((garrison) => (
          <div
            key={`grid-${garrison.id}`}
            className="w-full border-b border-[#E2E8F0]"
            style={{ height: `${ROW_HEIGHT}px` }}
          />
        ))}
      </div>

      <div
        className="absolute inset-0 z-10"
        onClick={(e) => {
          const chartInstance = chartRef.current;
          const pointInGrid = chartInstance?.convertFromPixel(
            {seriesIndex: 0},
            [e.nativeEvent.offsetX, e.nativeEvent.offsetY]
          );

          if (pointInGrid?.[0] !== undefined) {
            onSegmentClick?.(Math.round(pointInGrid[0]) * 60);
            return;
          }

          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const currentZoomStart = zoom?.start ?? 0;
          const currentZoomEnd = zoom?.end ?? 100;
          const totalMinutes = chartData.maxFilteredMinutes;
          const visibleMinutes = totalMinutes * ((currentZoomEnd - currentZoomStart) / 100);
          const startMinuteOffset = totalMinutes * (currentZoomStart / 100);
          const clickedMinute = startMinuteOffset + (x / rect.width) * visibleMinutes;

          onSegmentClick?.(Math.round(clickedMinute) * 60);
        }}
      >
        <SentryEchart
          title=""
          option={option}
          notMerge={true}
          onChartReady={(nextChartInstance) => {
            chartRef.current = nextChartInstance;
            setChartInstance(nextChartInstance);
          }}
          onEvents={echartsEvents}
        />
      </div>
    </div>
  );
};

export default memo(Timeline);
